<template>
    <form method="POST" @submit.prevent="submitForm">

        <!-- Шаг 1: Email -->
        <template v-if="!success_login.length">

            <h1 class="login-title">Welcome back</h1>
            <p class="login-desc">Enter your email to access your toolkits</p>

            <div class="alert alert-error" v-if="error_login.length">{{ error_login }}</div>

            <div class="login-field">
                <label class="login-label" for="loginEmail">Email address</label>
                <input
                    v-model="email"
                    class="login-input"
                    :class="{ 'input-error': error_login.length }"
                    id="loginEmail"
                    type="email"
                    placeholder="you@example.com"
                    autocomplete="email"
                    name="email"
                    required
                >
            </div>

            <button
                :class="`login-btn ${ isLoading ? 'is-loading' : '' }`"
                :disabled="isLoading"
                type="submit"
            >Sign in →</button>

            <button
                type="button"
                class="login-btn-back"
                @click="backToMall"
            >← Back to Mall</button>

            <div class="login-footer">
                Don't have an account? Just put your email in form
            </div>

        </template>

        <!-- Шаг 2: Код -->
        <template v-else>

            <h1 class="login-title">Enter your code</h1>
            <p class="login-desc">We sent a 6-digit code to your email</p>

            <div class="alert alert-error" v-if="error_login.length">{{ error_login }}</div>

            <div class="login-field">
                <label class="login-label">Verification code</label>
                <div class="login-code-inputs">
                    <input
                        v-for="(digit, index) in code"
                        :key="index"
                        :ref="el => { if (el) codeInputs[index] = el }"
                        v-model="code[index]"
                        type="text"
                        inputmode="numeric"
                        maxlength="1"
                        class="login-code-input"
                        :class="{ 'input-error': error_login.length }"
                        @input="onCodeInput( index, $event )"
                        @keydown="onCodeKeydown( index, $event )"
                        @paste="onCodePaste( $event )"
                        @focus="$event.target.select()"
                    >
                </div>
            </div>

            <button
                :class="`login-btn ${ isLoading ? 'is-loading' : '' }`"
                :disabled="isLoading || !isCodeComplete || blocked"
                type="button"
                @click="verifyCode"
            >Verify code →</button>

            <div class="login-footer" v-if="blocked">
                Too many attempts. Please
                <a :href="magicLink">click here to sign in</a>
                or check your email.
            </div>
            <div class="login-footer" v-else-if="time > 0">
                <div class="login-countdown">
                    <span class="login-countdown-num">{{ formattedTime }}</span>
                </div>
                Until the next email can be sent
            </div>
            <div class="login-footer" v-else>
                Didn't receive the code?
                <a href="#" @click.prevent="resendCode">Resend</a>
            </div>

        </template>

    </form>
</template>

<script>
import { defineComponent } from 'vue';
import { track, GTM_EVENTS } from '../../js/utils/gtm';

export default defineComponent({

    name: 'login',

    data() {
        return {
            isLoading     : false,
            email         : '',
            loginToken    : '',
            magicLink     : '',
            redirect      : new URLSearchParams( window.location.search ).get('redirect') || '',
            blocked       : false,
            code          : [ '', '', '', '', '', '' ],
            codeInputs    : [],
            error_login   : '',
            success_login : '',
            time          : 0,
            timerId       : null,
        };
    },

    computed: {

        isCodeComplete() {
            return this.code.every( digit => digit !== '' );
        },

        fullCode() {
            return this.code.join('');
        },

        formattedTime() {

            const m = Math.floor( this.time / 60 );
            const s = this.time % 60;

            return `${ m }:${ s.toString().padStart( 2, '0' ) }`;
        },
    },

    mounted() {
        this.$nextTick(() => {
            document.getElementById('loginEmail')?.focus();
        });
    },

    methods: {

        backToMall() {
            window.location.href = window.mall_domain || '/';
        },

        onCodeInput( index, event ) {

            const value = event.target.value;

            if( !/^\d*$/.test( value ) ) {
                this.code[ index ] = '';
                return;
            }

            if( value && index < 5 ) {
                this.$nextTick(() => {
                    this.codeInputs[ index + 1 ]?.focus();
                });
            }

            if( this.isCodeComplete )
                this.verifyCode();
        },

        onCodeKeydown( index, event ) {

            if( event.key === 'Backspace' && ! this.code[ index ] && index > 0 ) {
                this.$nextTick(() => {
                    this.codeInputs[ index - 1 ]?.focus();
                });
            }

            if( event.key === 'ArrowLeft' && index > 0 ) {
                event.preventDefault();
                this.codeInputs[ index - 1 ]?.focus();
            }

            if( event.key === 'ArrowRight' && index < 5 ) {
                event.preventDefault();
                this.codeInputs[ index + 1 ]?.focus();
            }
        },

        onCodePaste( event ) {

            event.preventDefault();
            const pastedData = event.clipboardData.getData('text').replace( /\D/g, '' ).slice( 0, 6 );

            if( ! pastedData )
                return;

            pastedData.split('').forEach( ( digit, i ) => {
                if( i < 6 )
                    this.code[ i ] = digit;
            });

            const nextIndex = Math.min( pastedData.length, 5 );
            this.$nextTick(() => {
                this.codeInputs[ nextIndex ]?.focus();
            });

            if( pastedData.length === 6 )
                this.$nextTick(() => this.verifyCode());
        },

        clearCode() {
            this.code = [ '', '', '', '', '', '' ];
            this.$nextTick(() => {
                this.codeInputs[ 0 ]?.focus();
            });
        },

        startTimer( seconds ) {

            this.time = seconds;

            if( this.timerId )
                clearInterval( this.timerId );

            this.timerId = setInterval(() => {
                this.time -= 1;
                if( this.time <= 0 ) {
                    clearInterval( this.timerId );
                    this.timerId = null;
                }
            }, 1000);
        },

        isValidEmail( email ) {
            return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test( email );
        },

        /**
         * Повторная отправка кода - алиас submitForm
         */
        resendCode() {

            if( this.time > 0 )
                return;

            this.blocked = false;
            this.clearCode();
            this.submitForm();
        },

        /**
         * Шаг 1 - отправка email → POST /auth/loginEmail
         */
        async submitForm() {

            if( this.isLoading )
                return;

            if( ! this.isValidEmail( this.email ) ) {
                this.error_login = 'The email field is filled in incorrectly.';
                return;
            }

            this.isLoading   = true;
            this.error_login = '';

            try {
                const response = await this.$http.post( '/auth/loginEmail', {
                    email    : this.email,
                    redirect : this.redirect,
                    w        : true,
                });
                const { data } = response.data;

                if( data?.status === 'error' ) {
                    this.error_login = data.message;
                    return;
                }

                if( data?.time ) {
                    this.loginToken    = data.token || '';
                    this.magicLink     = data.magic_link || '';
                    this.success_login = data.message;

                    // ① sign_in_requested - код/magic-link запрошен.
                    track( GTM_EVENTS.SIGN_IN_REQUESTED, { method: 'email_code' } );

                    this.startTimer( data.time );

                    this.$nextTick(() => {
                        this.codeInputs[ 0 ]?.focus();
                    });
                } else {
                    this.error_login = data.message || 'Something went wrong.';
                }

            } catch( err ) {
                console.error( 'error: ', err );
                this.error_login = 'Connection error. Please try again.';
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Шаг 2 - верификация кода -> POST /auth/verifyCode
         */
        async verifyCode() {

            if( this.isLoading || ! this.isCodeComplete || this.blocked )
                return;

            this.isLoading   = true;
            this.error_login = '';

            try {
                const response = await this.$http.post( '/auth/verifyCode', {
                    token    : this.loginToken,
                    code     : this.fullCode,
                    redirect : this.redirect,
                });
                const { data } = response.data;

                if( data?.status === 'error' ) {

                    // Попытки исчерпаны - блокируем форму
                    if( data?.code === 429 ) {
                        this.blocked     = true;
                        this.error_login = '';
                        return;
                    }

                    this.error_login = data.message || 'Invalid code. Please try again.';
                    this.clearCode();
                    return;
                }

                if( data?.token ) {

                    // Ставим новую с доменом
                    const expires = new Date( Date.now() + 86400 * 1000 ).toUTCString();
                    document.cookie = `${ window.auth_token }=${ data.token }; path=/; domain=.${ window.base_domain }; expires=${ expires }; secure; samesite=strict`;
                }

                if( data?.redirect )
                    // sign_up/login шлёт ga-auth-event.volt на приземлении: бэк уже дописал
                    // ?_ga_auth=...&_ga_m=code в redirect (authorizeUser), как для magic-link.
                    window.location.href = data.redirect;
                else
                    this.error_login = data.message || 'Something went wrong.';

            } catch( err ) {
                console.error( 'error: ', err );
                this.error_login = 'Connection error. Please try again.';
                this.clearCode();
            } finally {
                this.isLoading = false;
            }
        },
    },
});
</script>

<style scoped>
</style>